<template>
    <div class="close-header-title no-con">
        <section class="privacy-section">
            <div class="container">
                <div class="main-heading">
                    <h1>HOST TERMS & CONDITIONS vanovy</h1>
                    <p>Last updated: 7 September 2025</p>
                    <p>PLEASE READ CAREFULLY. These <a href="/host-terms-and-conditions" target="_blank">Host Terms &
                            Conditions</a> (“Host Terms”) govern your access to and use
                        of the Vanovy marketplace as a Host. “Vanovy”, “we”, “us”, or “our” refers to
                        the operator of the Platform located at <a href="https://www.vanovy.com"
                            target="_blank">www.vanovy.com</a> (the “Platform”). By creating a Host account,
                        listing a property, accepting or requesting a booking, receiving any payout, or otherwise using
                        the Platform in a capacity that offers accommodation, you agree to be legally bound by these
                        <a href="/host-terms-and-conditions" target="_blank">Host Terms</a>, our <a
                            href="/termsofservice" target="_blank">Terms of Service</a>, <a
                            href="/guest-terms-and-conditions" target="_blank">Guest Terms & Conditions</a>, <a
                            href="/cancellation-and-refund-policy" target="_blank">Cancellation & Refund Policy</a>,
                        <a href="/privacypolicy" target="_blank">Privacy Policy</a>, and <a href="/cookies-policy"
                            target="_blank">Cookie Policy</a> (collectively, the “Agreement”). In case of conflict,
                        these <a href="/host-terms-and-conditions" target="_blank">Host Terms</a> prevail for matters
                        specific to Hosts.
                    </p>
                    <p>Vanovy operates a global online marketplace facilitating medium-term property rentals
                        (1–12 months) between hosts and guests. We act solely as an intermediary—we do not own or manage
                        properties, we are not a broker, insurer, real estate agent, or property manager, and we do not
                        guarantee any outcome or placement in search results. The Platform’s governing law is Spain,
                        without prejudice to any mandatory local rules that apply to you.</p>
                </div>
                <div class="content-section center">
                    <h2>ARTICLE I: DEFINITIONS & INTERPRETATION</h2>
                    <h4>1.1 Definitions.</h4>
                    <p>(a) “Accommodation” means the premises you list for rent on the Platform.</p>
                    <p>(b) “Booking” means a reservation for Accommodation confirmed through the Platform.</p>
                    <p>(c) “Guest” means a person booking or occupying Accommodation via the Platform.</p>
                    <p>(d) “Host”, “you”, or “your” means the person or entity listing, offering, or providing
                        Accommodation through the Platform.</p>
                    <p>(e) “Host Fee” means the service fee payable to Vanovy, currently 15% of the applicable
                        booking amounts; we cover Stripe processing costs for in-Platform transactions.</p>
                    <p>(f) “Payout” means funds we disburse to you (net of fees, adjustments, withholdings, and setoffs)
                        for a Booking.</p>
                    <p>(g) “House Rules” means host-specified rules applicable to an Accommodation, as disclosed on the
                        listing and during checkout.</p>
                    <p>(h) “Sanctions/KYC” means sanctions, anti-money laundering, counter-terrorist financing, and
                        “know-your-customer/beneficial owner” checks.</p>
                    <p>(i) “Taxes” includes VAT/GST/sales tax, occupancy/room taxes, tourist levies, income taxes, and
                        any mandatory reporting regimes (including DAC7 where applicable).</p>
                    <p>(j) “Platform Content” means all Platform UI, code, databases, ranking and matching systems,
                        brands, and materials owned or controlled by Vanovy.</p>
                    <h4>1.2 Interpretation.</h4>
                    <p>Words importing the singular include the plural and vice versa. References to “including” mean
                        “including without limitation.” These <a href="/host-terms-and-conditions" target="_blank">Host
                            Terms</a> are construed to the maximum extent favorable
                        to Vanovy as permitted by applicable law.</p>

                    <h2>ARTICLE II: HOST ELIGIBILITY, ACCOUNT & VERIFICATION</h2>
                    <h4>2.1 Eligibility.</h4>
                    <p>You represent and warrant that you:</p>
                    <p>(1) are 18 years of age or older and have full legal capacity;</p>
                    <p>(2) have the legal right to rent the Accommodation (including under leases, mortgages,
                        HOA/condo/co-op rules, zoning, use-class, and co-tenancy laws); and</p>
                    <p>(3) will comply with all permits, registrations, and local requirements applicable to medium-term
                        rentals.</p>
                    <h4>2.2 Business User Status.</h4>
                    <p>Where you use the Platform in a professional or business capacity, you acknowledge and agree that
                        you are a business user and these <a href="/host-terms-and-conditions" target="_blank">Host
                            Terms</a> are business-to-business terms. Consumer
                        protections that apply only to consumers do not apply to you.</p>
                    <h4>2.3 Registration & Verification.</h4>
                    <p>We may require at any time:</p>
                    <p>(1) identity verification (government ID, selfie match, address checks);</p>
                    <p>(2) banking and payout verification;</p>
                    <p>(3) beneficial owner disclosures for entities;</p>
                    <p>(4) Sanctions/KYC screening; and</p>
                    <p>(5) proof of authorization to list (e.g., landlord’s consent).</p>
                    <p>We may withhold, delay, or refuse Payouts and suspend or terminate accounts where verification is
                        incomplete, inconsistent, or unsatisfactory in our discretion, subject to applicable
                        Platform-to-Business (“P2B”) rules in the EU as set out in Article XX.</p>
                    <h4>2.4 Accuracy & Updates.</h4>
                    <p>You must maintain accurate account, contact, tax, and payout information and promptly update any
                        changes. You authorize us to re-verify and to request additional information from you or
                        third-party sources to maintain compliance and platform integrity.</p>

                    <h2>ARTICLE III: LISTINGS: ACCURACY, CONDITION & DISCLOSURES</h2>
                    <h4>3.1 Listing Accuracy.</h4>
                    <p>You must ensure that all listing content is truthful, current, and non-misleading, including:
                        photos, amenities, capacity, number and type of rooms, bed counts, accessibility features,
                        safety equipment, neighborhood, exact or proximate location as permitted by local law, and any
                        material conditions or defects.</p>
                    <h4>3.2 Photographs & Media.</h4>
                    <p>Images and videos must accurately depict the Accommodation. Staged or representational media must
                        be clearly labeled; no image may materially misrepresent size, condition, or amenities. You
                        grant us a non-exclusive, worldwide, transferable, sublicensable, royalty-free license to use,
                        reproduce, modify, distribute, publicly display, translate, and create derivative works from
                        your listing content for Platform operation, marketing (on or off Platform), trust & safety, and
                        legal compliance (see Article XII).</p>
                    <h4>3.3 Habitability & Safety.</h4>
                    <p>You are solely responsible for providing habitable, clean, and safe premises meeting all legal
                        standards for medium-term stays, including working smoke and, where required, carbon monoxide
                        detectors, fire extinguishers, safe utilities, adequate locks, and any additional equipment
                        required by local law. You must promptly remedy hazards and maintain the property in good order.
                    </p>
                    <h4>3.4 Required Disclosures.</h4>
                    <p>You are responsible for all mandatory disclosures (e.g., lead paint, local registration numbers,
                        building rules, elevator notices, energy certificates) and must display such identifiers on your
                        listing if required by law.</p>
                    <h4>3.5 Privacy Devices & Surveillance.</h4>
                    <p>(1) Hidden cameras or covert surveillance is strictly prohibited anywhere on the premises.</p>
                    <p>(2) Visible devices (e.g., exterior-only or common-area video without audio, where lawful)
                        require clear disclosure in the listing and in-premises signage and must not be placed in any
                        private areas (bedrooms, bathrooms, or any space where a Guest reasonably expects privacy).</p>
                    <p>(3) You are solely responsible for compliance with local recording and notification laws.</p>

                    <h2>ARTICLE IV: MANDATORY LOCAL COMPLIANCE</h2>
                    <h4>4.1 Licenses & Registrations.</h4>
                    <p>You must obtain and maintain all licenses, registrations, and authorizations required for
                        medium-term rentals and display any required registration numbers on the listing.</p>
                    <h4>4.2 Zoning, Caps & Use Restrictions.</h4>
                    <p>You must comply with zoning/use-class restrictions, principal residence rules (if any), occupancy
                        caps, and any neighborhood or building rules.</p>
                    <h4>4.3 Health, Safety & Accessibility.</h4>
                    <p>You must comply with applicable health and safety laws and make reasonable efforts to provide
                        accessible features consistent with applicable law and your listing description. You must not
                        misrepresent accessibility.</p>
                    <h4>4.4 Non-Discrimination.</h4>
                    <p>You must not discriminate on prohibited grounds. For listings in the U.S., you must comply with
                        Fair Housing obligations; for listings in the EU, comply with applicable equality and
                        anti-discrimination rules. House Rules must not contain unlawful restrictions.</p>
                    <h4>4.5 Inspections & Proof.</h4>
                    <p>Upon reasonable request, you agree to provide proof of compliance (e.g., registration
                        certificate, inspection report, insurance confirmation). Failure to provide proof may lead to
                        listing removal, suspension, or payout holds.</p>

                    <h2>ARTICLE V: HOUSE RULES</h2>
                    <h4>5.1 Drafting and Disclosure.</h4>
                    <p>You may adopt House Rules consistent with law and these <a href="/host-terms-and-conditions"
                            target="_blank">Host Terms</a>. Rules must be clear, lawful,
                        non-discriminatory, and disclosed on the listing prior to booking.</p>
                    <h4>5.2 Changes to Rules.</h4>
                    <p>Material changes that tighten obligations on Guests apply only to future bookings after the
                        change is published. For existing bookings, changes require the Guest’s written consent via the
                        Platform.</p>
                    <h4>5.3 Enforcement.</h4>
                    <p>You are responsible for enforcing House Rules in a lawful manner. We may assist in dispute
                        resolution but are not obligated to act as an adjudicator and are not liable for Guests’
                        compliance.</p>

                    <h2>ARTICLE VI: PRICING, CHARGES & TAXES</h2>
                    <h4>6.1 Host Sets Price.</h4>
                    <p>Except as permitted under promotional programs you opt-into, you set the nightly/monthly price
                        and any lawful additional fees (e.g., cleaning or utilities), which must be transparent and
                        included in the listing.</p>
                    <h4>6.2 Host Fee; Stripe Costs.</h4>
                    <p>You acknowledge the 15% Host Fee. We cover Stripe processing costs for transactions completed
                        through the Platform. Currency conversion, bank wire, or intermediary bank fees, and chargeback
                        fees may apply and may be deducted from Payouts.</p>
                    <h4>6.3 Taxes—Responsibility.</h4>
                    <p>You are solely responsible for determining, collecting (where applicable), reporting, and
                        remitting all Taxes, including VAT/GST, occupancy/tourist taxes, and income taxes. Where law or
                        program design requires, you authorize us to calculate, collect, and/or remit certain Taxes on
                        your behalf, using data you provide.</p>
                    <h4>6.4 DAC7 & Reporting Regimes.</h4>
                    <p>Where platform reporting applies (e.g., DAC7 in the EU or analogous regimes), you agree to timely
                        provide accurate tax and identification information, including national ID, tax ID, place of
                        establishment/residence, and payout details. You agree that we may report your relevant data and
                        revenues to the competent authorities and withhold Payouts or suspend your listings until
                        compliant.</p>
                    <h4>6.5 Utilities & Fair Use.</h4>
                    <p>If utilities are included in the price, you must disclose any fair-use caps or metering. Where
                        caps apply, you may charge Guests for documented excess usage, subject to our approval and
                        post-stay claim process (Article VIII).</p>

                    <h2>ARTICLE VII: AVAILABILITY, BOOKINGS & CALENDAR INTEGRITY</h2>
                    <h4>7.1 Calendar Management.</h4>
                    <p>You must maintain an accurate calendar, promptly update availability, and honor all confirmed
                        Bookings. Overbooking is prohibited; if it occurs, you must remedy as set out in Article IX.</p>
                    <h4>7.2 Booking Acceptance.</h4>
                    <p>For request-based listings, you must respond to Guest requests promptly. Unreasonable or
                        discriminatory rejections may result in ranking penalties or suspension.</p>
                    <h4>7.3 Instant Bookings.</h4>
                    <p>If you enable Instant Book, you agree to automatic acceptance for qualifying Guests (e.g.,
                        verified identity, no risk flags). You may set reasonable filters (e.g., minimum stay).</p>
                    <h4>7.4 Booking Formation.</h4>
                    <p>A Booking is binding when the Platform issues a confirmation. You must provide access as promised
                        and comply with the terms, including House Rules and price breakdown.</p>

                    <h2>ARTICLE VIII: SECURITY DEPOSITS, DAMAGES & CLAIMS</h2>
                    <h4>8.1 Deposits.</h4>
                    <p>You may require a reasonable Security Deposit, shown at checkout and charged or pre-authorized
                        through the Platform where enabled.</p>
                    <h4>8.2 Claims Process.</h4>
                    <p>For damage, excess cleaning, missing items, or rule violations, you must submit a claim within
                        the timeframe set by Platform tools (typically 72 hours after check-out) with time-stamped
                        evidence, itemized invoices/estimates, and a description. We may request Guest response and
                        evaluate per our policies.</p>
                    <h4>8.3 Outcomes.</h4>
                    <p>If approved, we will apply the Security Deposit or charge the Guest, then disburse to you net of
                        fees. Recovery is not guaranteed; you should maintain appropriate insurance. Our decision is
                        final for Platform purposes but does not limit external legal rights.</p>
                    <h4>8.4 Utilities Claims.</h4>
                    <p>For excess utilities beyond a disclosed cap, provide meter readings and bills. Claims must be
                        reasonable and pro-rated.</p>
                    <h4>8.5 Fair Wear & Tear.</h4>
                    <p>Ordinary wear and tear is not chargeable.</p>

                    <h2>ARTICLE IX: CANCELLATIONS & REFUNDS</h2>
                    <h4>9.1 Policy Application.</h4>
                    <p>Cancellations and refunds are governed by the <a href="/cancellation-and-refund-policy"
                            target="_blank">Cancellation & Refund Policy</a>, which you acknowledge
                        and agree to. In summary:</p>
                    <p>(a) Guest cancels ≥30 days before check-in: full refund; you receive nothing for that Booking.
                    </p>
                    <p>(b) Guest cancels &lt;30 days: first Billing Cycle non-refundable; 50% of accommodation price to
                        you, 50% refunded to Guest after platform fee deduction.</p>
                    <p>(c) Guest claims Valid Reason within 24 hours after check-in: full refund to Guest if uncured;
                        you receive nothing or reduced amount.</p>
                    <p>(d) Mid-stay issues after 24 hours: generally non-refundable unless you agree or law requires.
                    </p>
                    <h4>9.2 Host Cancellation Penalties.</h4>
                    <p>If you cancel, the Guest receives a full refund. You may incur penalties including ranking
                        reduction, suspension, and charges for relocation costs we incur.</p>
                    <h4>9.3 Overbooking & Access Failure.</h4>
                    <p>If you overbook or fail to provide access, you must provide comparable accommodation at no extra
                        cost to the Guest or agree to a full refund. Failure may result in penalties and Payout
                        deductions.</p>
                    <h4>9.4 Force Majeure.</h4>
                    <p>If force majeure prevents performance, we may cancel with pro rata refunds; no penalties apply
                        unless you were at fault.</p>

                    <h2>ARTICLE X: PAYOUTS & TIMING</h2>
                    <h4>10.1 Payout Release.</h4>
                    <p>Payouts are released approximately 24 hours after successful Guest check-in, net of Host Fee,
                        Taxes (where we collect), adjustments, and withholdings.</p>
                    <h4>10.2 Holds & Delays.</h4>
                    <p>We may hold or delay Payouts pending verification, dispute resolution, fraud checks, or
                        compliance (e.g., Sanctions/KYC, DAC7).</p>
                    <h4>10.3 Chargebacks.</h4>
                    <p>If a Guest chargeback is upheld, we may deduct the amount (plus fees) from your future Payouts or
                        require reimbursement. You must cooperate in evidence submission.</p>
                    <h4>10.4 Setoffs.</h4>
                    <p>We may set off amounts you owe us (e.g., for penalties, refunds, or negative balances) against
                        Payouts.</p>
                    <h4>10.5 Currency & Fees.</h4>
                    <p>Payouts are in your chosen currency. Conversion, wire, or bank fees may apply and are your
                        responsibility.</p>

                    <h2>ARTICLE XI: REVIEWS & REPUTATION</h2>
                    <h4>11.1 Reviews.</h4>
                    <p>Guests may leave reviews after checkout. We may moderate for policy violations, fraud, or abuse.
                        You may not coerce, bribe, or retaliate for reviews.</p>
                    <h4>11.2 Reputation Impact.</h4>
                    <p>Reviews, cancellations, and claim history may impact your ranking and visibility. We do not
                        guarantee any specific placement.</p>

                    <h2>ARTICLE XII: CONTENT & INTELLECTUAL PROPERTY</h2>
                    <h4>12.1 Your Content License.</h4>
                    <p>You grant Vanovy a worldwide, perpetual, irrevocable, transferable, sublicensable,
                        royalty-free license to use, host, reproduce, adapt, translate, distribute, publicly display,
                        perform, and create derivative works from your listing content (text, photos, videos) for
                        Platform operation, marketing (on and off Platform), trust & safety, and legal compliance.</p>
                    <h4>12.2 Representations.</h4>
                    <p>You represent that your content is accurate, lawful, and does not infringe third-party rights
                        (IP, privacy, publicity).</p>
                    <h4>12.3 Platform IP.</h4>
                    <p>You may not use, copy, or imitate Platform Content without our written consent. All rights in the
                        Platform are reserved.</p>
                    <h4>12.4 Takedown.</h4>
                    <p>We may remove or disable content that violates policy or law, subject to P2B notice/reason rules
                        where applicable.</p>

                    <h2>ARTICLE XIII: PROHIBITED ACTIVITIES</h2>
                    <h4>13.1 Prohibited Conduct.</h4>
                    <p>You must not:</p>
                    <p>(a) engage in fraud, misrepresentation, or chargeback abuse;</p>
                    <p>(b) use hidden cameras or covert surveillance;</p>
                    <p>(c) violate non-discrimination or accessibility laws;</p>
                    <p>(d) host unlawful parties or overcrowding;</p>
                    <p>(e) sublet or assign Bookings without consent;</p>
                    <p>(f) tamper with safety devices;</p>
                    <p>(g) facilitate trafficking, exploitation, or criminal conduct;</p>
                    <p>(h) post malware, scrape, reverse engineer, or disrupt the Platform; or</p>
                    <p>(i) circumvent the Platform by moving discovered transactions off-Platform.</p>
                    <h4>13.2 Anti-Circumvention.</h4>
                    <p>All transactions discovered through the Platform must be completed through the Platform. We may
                        assess liquidated damages equal to the Host Fee on the booking value (including extensions) plus
                        investigation costs, and suspend/terminate accounts for circumvention.</p>

                    <h2>ARTICLE XIV: DATA USE & PRIVACY</h2>
                    <h4>14.1 Privacy Policy.</h4>
                    <p>Our <a href="/privacypolicy" target="_blank">Privacy Policy</a> governs our data practices,
                        including Sanctions/KYC screening, fraud
                        detection, analytics, and payouts. You agree to the <a href="/privacypolicy"
                            target="_blank">Privacy Policy</a> by using the Platform.</p>
                    <h4>14.2 Guest Data.</h4>
                    <p>You may use Guest data only for the Booking, property management, and legal compliance.
                        Independent marketing requires separate Guest consent. You act as an independent controller for
                        your processing and must provide any required notices.</p>
                    <h4>14.3 Messaging Review.</h4>
                    <p>For trust & safety and compliance, we may scan or review communications on the Platform.</p>

                    <h2>ARTICLE XV: TAX REPORTING & DAC7</h2>
                    <h4>15.1 DAC7 (EU).</h4>
                    <p>If you are in the EU or list in the EU, you must provide accurate tax/KYC data for DAC7 reporting
                        (identity, tax ID, residency, bank info). We may report your revenues and details to tax
                        authorities and withhold Payouts for non-compliance.</p>
                    <h4>15.2 Other Regimes.</h4>
                    <p>Similar reporting may apply elsewhere (e.g., IRS 1099 in the U.S.). You must provide data and
                        consent to reporting as required.</p>

                    <h2>ARTICLE XVI: INSURANCE & RISK ALLOCATION</h2>
                    <h4>16.1 No Insurance Provided.</h4>
                    <p>We do not provide insurance. You must maintain adequate liability, property, and business
                        interruption coverage appropriate for medium-term rentals.</p>
                    <h4>16.2 Risk Allocation.</h4>
                    <p>You bear all risk related to your Accommodation, including damage, injuries, hazards, and
                        third-party claims. We are not liable for such risks.</p>

                    <h2>ARTICLE XVII: SUSPENSION & TERMINATION</h2>
                    <h4>17.1 Grounds.</h4>
                    <p>We may suspend or terminate your account or listings immediately for: (a) fraud or safety
                        incidents; (b) repeated violations; (c) verification failure; (d) Sanctions/KYC concerns; or (e)
                        unacceptable risk.</p>
                    <h4>17.2 Effects.</h4>
                    <p>Suspension/termination may include removal of listings, cancellation of bookings (with Guest
                        refunds), Payout holds, and offsets for amounts owed.</p>
                    <h4>17.3 EU P2B Notice.</h4>
                    <p>For EU Hosts, we provide a statement of reasons and internal appeal per Article XX.</p>
                    <h4>17.4 Survival.</h4>
                    <p>Provisions that by nature survive (indemnities, limitations, IP licenses, data retention)
                        continue after termination.</p>

                    <h2>ARTICLE XVIII: PLATFORM CHANGES & BETA</h2>
                    <h4>18.1 Changes.</h4>
                    <p>We may modify features or algorithms. For EU Hosts, material changes follow P2B notice (Article
                        XX).</p>
                    <h4>18.2 Beta Features.</h4>
                    <p>Beta-labeled features are provided “as is” and may be unstable or discontinued.</p>

                    <h2>ARTICLE XIX: RANKING & VISIBILITY</h2>
                    <h4>19.1 Factors.</h4>
                    <p>Without limiting our discretion, ranking may consider: listing quality, calendar integrity,
                        responsiveness/acceptance, reviews/complaints, cancellations/access failures, price/value,
                        safety verifications, and user preferences. Paid promotions (if offered) will be labeled.</p>
                    <h4>19.2 No Guarantee.</h4>
                    <p>We do not guarantee specific placement or visibility. Ranking is algorithmic and may change.</p>

                    <h2>ARTICLE XX: EU PLATFORM-TO-BUSINESS (P2B) RULES</h2>
                    <h4>20.1 Scope.</h4>
                    <p>If you are an EU business Host, P2B Regulation (EU 2019/1150) applies to our intermediation
                        services.</p>
                    <h4>20.2 Ranking Parameters.</h4>
                    <p>Main parameters: listing completeness, price competitiveness, responsiveness, review scores,
                        cancellation rates, compliance verifications, safety equipment. No paid ranking influence unless
                        labeled.</p>
                    <h4>20.3 Internal Complaint Handling.</h4>
                    <p>Submit complaints via <a href="mailto:support@vanovy.com"
                            target="_blank">support@vanovy.com</a> (subject: “P2B Complaint”). We acknowledge and
                        respond within 15 business days.</p>
                    <h4>20.4 Mediation.</h4>
                    <p>If unresolved, we will engage in mediation with EU-recognized bodies agreed in good faith.
                        Mediation is optional and non-exclusive.</p>
                    <h4>20.5 Changes Notice.</h4>
                    <p>We provide at least 15 days’ notice for material changes, unless urgent for security/legal
                        reasons or beneficial/neutral.</p>
                    <h4>20.6 Suspension/Termination Reasons.</h4>
                    <p>We provide a statement of reasons for restrictions, suspensions, or terminations, unless
                        prohibited by law/security. You may appeal via internal complaints.</p>

                    <h2>ARTICLE XXI: INDEMNITIES</h2>
                    <h4>21.1 Indemnity.</h4>
                    <p>You agree to defend, indemnify, and hold harmless Vanovy, its affiliates, and their
                        officers, directors, employees, and agents from all claims, damages, losses, liabilities, costs,
                        and expenses (including reasonable attorneys’ fees) arising from or related to:</p>
                    <p>(1) your Accommodation (including property conditions, habitability, hazards, injuries, and
                        property damage);</p>
                    <p>(2) your breach of law, permits, leases, HOA/mortgage rules, or third-party rights (including IP,
                        privacy, publicity, or non-discrimination rights);</p>
                    <p>(3) Taxes (including assessments, penalties, interest, and reporting failures);</p>
                    <p>(4) your content or misuse of Guest data;</p>
                    <p>(5) cancellations, overbooking, access failures, or failure to honor bookings;</p>
                    <p>(6) your negligent or willful acts or omissions; and</p>
                    <p>(7) your breach of this Agreement.</p>
                    <h4>21.2 Defense & Settlements.</h4>
                    <p>We may elect to control the defense in our discretion. You will not settle any matter without our
                        prior written consent if it imposes obligations on us or does not fully release us.</p>

                    <h2>ARTICLE XXII: DISCLAIMERS</h2>
                    <h4>22.1 Marketplace Only.</h4>
                    <p>The Platform is a marketplace. We do not control or guarantee the existence, quality, safety,
                        legality, habitability, or suitability of any Accommodation or the conduct of any user.</p>
                    <h4>22.2 No Professional Advice.</h4>
                    <p>Information on the Platform is for general purposes and not legal, tax, insurance, or compliance
                        advice. You should obtain your own professional advice.</p>
                    <h4>22.3 “As Is.”</h4>
                    <p>The Platform and services are provided “as is” and “as available.” To the maximum extent
                        permitted by law, we disclaim all warranties, express or implied, including merchantability,
                        fitness for a particular purpose, quiet enjoyment, non-infringement, and any warranties arising
                        out of course of dealing or usage of trade.</p>

                    <h2>ARTICLE XXIII: LIMITATION OF LIABILITY</h2>
                    <h4>23.1 Types of Damages Excluded.</h4>
                    <p>To the maximum extent permitted by law, Vanovy shall not be liable for any indirect,
                        incidental, special, exemplary, punitive, or consequential damages, including loss of profits,
                        revenue, goodwill, data, or business interruption, even if advised of the possibility of such
                        damages.</p>
                    <h4>23.2 Aggregate Cap.</h4>
                    <p>To the maximum extent permitted by law, our aggregate liability to you for all claims arising out
                        of or relating to this Agreement shall not exceed the total Host Fees paid by you to us in the
                        twelve (12) months preceding the event giving rise to liability.</p>
                    <h4>23.3 Multiple Claims.</h4>
                    <p>The existence of more than one claim does not enlarge the cap. The cap is a cumulative limit and
                        not per incident.</p>
                    <!-- <h4>23.4 Mandatory Laws.</h4>
                    <p>Nothing in this Article limits or excludes liability that cannot be excluded under mandatory law,
                        including liability for death or personal injury caused by negligence, fraud or fraudulent
                        misrepresentation, or willful misconduct.</p> -->

                    <h2>ARTICLE XXIV: FORCE MAJEURE</h2>
                    <h4>24.1 Force Majeure.</h4>
                    <p>Neither party is liable for failure or delay caused by events beyond reasonable control,
                        including natural disasters, epidemics, war, terrorism, civil unrest, labor disputes, utility
                        failures, communications outages, or governmental actions. During such events, we may suspend
                        features, hold Payouts where necessary, and/or cancel affected bookings with appropriate Guest
                        remedies as per policy.</p>

                    <h2>ARTICLE XXV: CHANGES TO SERVICES & TERMS</h2>
                    <h4>25.1 Service Modifications.</h4>
                    <p>We may modify, suspend, or discontinue any feature of the Platform at any time to maintain
                        security, comply with law, manage capacity, or improve the service.</p>
                    <h4>25.2 Terms Updates.</h4>
                    <p>We may update these <a href="/host-terms-and-conditions" target="_blank">Host Terms</a>. For EU
                        Hosts, Article XX applies. For non-EU Hosts, we will
                        provide notice by posting the updated terms and/or through the Platform or email. Your continued
                        use after the effective date constitutes acceptance.</p>

                    <h2>ARTICLE XXVI: COMMUNICATIONS & NOTICES</h2>
                    <h4>26.1 Electronic Communications.</h4>
                    <p>You agree to receive communications electronically, including notices, statements of reasons,
                        policy updates, and disclosures, via the Platform or email.</p>
                    <h4>26.2 Contact.</h4>
                    <p>Our primary contact is <a href="mailto:support@vanovy.com"
                            target="_blank">support@vanovy.com</a>. You must keep your email and phone number
                        current and monitor Platform notifications.</p>

                    <h2>ARTICLE XXVII: GOVERNING LAW & DISPUTE RESOLUTION</h2>
                    <h4>27.1 Governing Law.</h4>
                    <p>These <a href="/host-terms-and-conditions" target="_blank">Host Terms</a> are governed by the
                        laws of Spain, without regard to conflict-of-law principles,
                        and subject to mandatory local rules that cannot be derogated.</p>
                    <h4>27.2 Arbitration for Non-EU Business Hosts.</h4>
                    <p>Except where prohibited by law, any dispute with a Host outside the EU/EEA/UK shall be finally
                        resolved by binding arbitration seated in Madrid, Spain, in English, under a recognized arbitral
                        institution’s rules. Judgment on the award may be entered in any court of competent
                        jurisdiction.</p>
                    <h4>27.3 Courts & Injunctive Relief.</h4>
                    <p>Either party may seek injunctive or equitable relief in the courts of Madrid, Spain to protect
                        IP, confidentiality, or system integrity. For EU Hosts, recourse to competent courts remains
                        available and unaffected by mediation (Article XX).</p>
                    <h4>27.4 Time Limit.</h4>
                    <p>Any claim must be filed within one (1) year after the cause of action accrued or be permanently
                        barred, unless a longer period is mandatorily required by law.</p>

                    <h2>ARTICLE XXVIII: CONFIDENTIALITY</h2>
                    <h4>28.1 Scope.</h4>
                    <p>Non-public information learned through the Platform about our systems, fees, policies, security
                        practices, or other users (including Guests’ personal data) is confidential.</p>
                    <h4>28.2 Obligations.</h4>
                    <p>You will not disclose or use Confidential Information except as necessary to perform under this
                        Agreement or as required by law (with prompt notice to us, where lawful, to allow protective
                        measures).</p>

                    <h2>ARTICLE XXIX: GENERAL</h2>
                    <h4>29.1 Assignment.</h4>
                    <p>We may assign or transfer this Agreement freely. You may not assign or transfer without our prior
                        written consent.</p>
                    <h4>29.2 Relationship.</h4>
                    <p>No joint venture, partnership, employment, or agency relationship exists between you and us. You
                        act independently and on your own account.</p>
                    <h4>29.3 Severability.</h4>
                    <p>If any provision is held invalid, the remainder remains in effect, and a valid term reflecting
                        the parties’ intent will be substituted.</p>
                    <h4>29.4 No Waiver.</h4>
                    <p>Failure to enforce any provision is not a waiver of future enforcement.</p>
                    <h4>29.5 Survival.</h4>
                    <p>Articles IX–XII, XIV–XXIII, XXVII–XXIX, and any other terms that by their nature should survive,
                        survive termination.</p>

                    <h2>ARTICLE XXX: PRACTICAL GUIDANCE & EXAMPLES (NON-EXHAUSTIVE)</h2>
                    <h4>30.1 Example—Permit Duty.</h4>
                    <p>Your city requires a medium-term rental permit and to display the number on any advertisement.
                        You must obtain the permit, keep it valid, and display the number on your listing. If a Guest is
                        fined due to your failure, you must indemnify us and may be subject to rank penalties or
                        suspension.</p>
                    <h4>30.2 Example—Overbooking.</h4>
                    <p>You forgot to block dates already booked elsewhere and cannot host the arriving Guest. You must
                        either (a) provide an equivalent or better unit at no extra cost to the Guest; or (b) agree that
                        we may relocate the Guest and deduct the cost (including fees and reasonable administration)
                        from your Payouts.</p>
                    <h4>30.3 Example—Damage Claim.</h4>
                    <p>A Guest leaves significant damage. You submit a claim within 72 hours with photos, receipts, and
                        a description. We request Guest response and evaluate. If we can collect from the Guest, we will
                        disburse to you. If not, we are not liable for unrecovered amounts.</p>
                    <h4>30.4 Example—Circumvention.</h4>
                    <p>A Guest messages you asking to pay off-platform. You must decline, direct them to book through
                        the Platform, and report the attempt. If you accept off-platform payment, we may charge
                        liquidated damages equal to the Host Fee on the booking value plus investigation costs and may
                        suspend your account.</p>
                    <h4>30.5 Example—Guest Data.</h4>
                    <p>You download a Guest’s email and add it to your marketing list without consent. This violates
                        these <a href="/host-terms-and-conditions" target="_blank">Host Terms</a> and privacy laws. We
                        may suspend your account, require remedial steps, and you
                        must indemnify us for claims.</p>

                    <h2>ARTICLE XXXI: COUNTRY/REGION-SPECIFIC ADDENDA (HIGH-LEVEL)</h2>
                    <h4>31.1 EU/EEA & UK.</h4>
                    <p>(1) P2B disclosures and complaint handling apply (Article XX).</p>
                    <p>(2) VAT treatment varies; you are responsible for your own VAT/GST obligations and for providing
                        accurate tax data for DAC7 reporting.</p>
                    <p>(3) Non-essential cookies and marketing consents must follow local law.</p>
                    <p>(4) If and when we offer the Services to residents of the United Kingdom or monitor their
                        behavior, we will appoint a local representative in the UK as required by the UK GDPR. Contact
                        details (if appointed) will be available at <a href="mailto:support@vanovy.com"
                            target="_blank">support@vanovy.com</a>.</p>
                    <h4>31.2 United States.</h4>
                    <p>(1) Comply with Fair Housing rules where applicable.</p>
                    <p>(2) Occupancy and local hotel/STR laws vary; you must ensure compliance and collect/remit local
                        transient or occupancy taxes where required or cooperate where we collect/remit.</p>
                    <h4>31.3 Brazil & LATAM.</h4>
                    <p>(1) Ensure compliance with local rental classifications and registration.</p>
                    <p>(2) Maintain liability coverage appropriate for medium-term stays.</p>
                    <p>(3) Provide tax IDs and cooperate with reporting regimes.</p>
                    <h4>31.4 APAC.</h4>
                    <p>(1) Observe local caps, zoning, and licensing rules.</p>
                    <p>(2) Comply with building and safety standards and any mandatory disclosure requirements.</p>

                    <h2>ARTICLE XXXII: COMMUNICATIONS & NOTICES</h2>
                    <h4>32.1 Contact.</h4>
                    <p>For all hosting and compliance matters, contact <a href="mailto:support@vanovy.com"
                            target="_blank">support@vanovy.com</a>.</p>
                    <h4>32.2 Language.</h4>
                    <p>These <a href="/host-terms-and-conditions" target="_blank">Host Terms</a> are provided in
                        English. Where translations exist, the English version controls
                        to the extent permitted by law.</p>

                    <h2>ACCEPTANCE</h2>
                    <p>By creating a Host account, publishing a listing, or accepting a Booking, you acknowledge and
                        agree to these <a href="/host-terms-and-conditions" target="_blank">Host Terms & Conditions</a>
                        in full. You further acknowledge that Vanovy is a
                        marketplace intermediary, that you are responsible for legal compliance, safety, and Taxes, and
                        that Vanovy’ liability is limited as set out above.</p>
                </div>
            </div>
        </section>
    </div>
</template>
<script>
export default {
    name: 'hostTermsConditions',
    data() {
        return {};
    },
    mounted() { },
    methods: {},
    computed: {},
    created() { }
}
</script>